import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import styled from "styled-components";
import Overlay from "../../Componentes/Overlay";
import Modal from "../../Componentes/Utils/Modal";
import Formulario from "../../Componentes/Utils/Formulario";
import FormComp from "../../Componentes/FormComp";
import useApi from "../../hooks/useApi";
import ModalAdicionarLicencasColaborador from "./ModalAdicionarLicencasColaborador";

const LICENCAS_ENDPOINT = "/Painel/API/InventarioTI/GetLicencasColaborador/";
const REMOVE_LICENCA_ENDPOINT = "/Painel/API/InventarioTI/RemoveColabLicenca/";
const UPDATE_CONTATO_ENDPOINT = "/Painel/API/InventarioTI/UpdateColabLicencaContato/";
const UPDATE_EXCECAO_ENDPOINT = "/Painel/API/InventarioTI/UpdateColabLicencaExcecao/";

const InfoGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 0 16px;

  @media (max-width: 720px) {
    grid-template-columns: 1fr;
  }
`;

const SharedFields = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0;
  margin-top: 4px;

  input[type="email"] {
    min-height: 42px;
    font-size: 14px;
  }
`;

const LargeTextareaWrap = styled.div`
  textarea {
    min-height: 120px;
    resize: vertical;
    font-size: 14px;
    line-height: 1.45;
  }
`;

const SectionTitle = styled.p`
  margin: 8px 0 10px;
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 0.5px;
  color: #263b4b;
`;

const TableShell = styled.div`
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  overflow: auto;
  max-height: 320px;
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  font-size: 13px;
  color: #263b4b;

  thead {
    background: #f8fafc;
    position: sticky;
    top: 0;
    z-index: 1;
  }

  th,
  td {
    padding: 10px 12px;
    border-bottom: 1px solid #eef2f7;
    text-align: left;
    vertical-align: middle;
  }

  th {
    font-size: 11px;
    letter-spacing: 0.4px;
    color: #475569;
  }

  tbody tr:hover {
    background: #f8fafc;
  }
`;

const DeleteButton = styled.button`
  all: unset;
  cursor: pointer;
  color: #b91c1c;
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 0.3px;
  padding: 6px 10px;
  border-radius: 6px;
  transition: background 0.15s ease;

  &:hover {
    background: rgba(185, 28, 28, 0.08);
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const EmptyRow = styled.div`
  padding: 24px 12px;
  text-align: center;
  font-size: 13px;
  color: #64748b;
`;

const AddLicencasButton = styled.button`
  all: unset;
  box-sizing: border-box;
  cursor: pointer;
  width: 100%;
  margin-top: 12px;
  padding: 12px 16px;
  border-radius: 10px;
  border: 1px dashed #94a3b8;
  background: #f8fafc;
  color: #1d4ed8;
  font-size: 13px;
  font-weight: 700;
  letter-spacing: 0.4px;
  text-align: center;
  transition: background 0.15s ease, border-color 0.15s ease;

  &:hover {
    background: #eff6ff;
    border-color: #2563eb;
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const DeAcordoRow = styled.label`
  display: flex;
  align-items: flex-start;
  gap: 10px;
  margin: 12px 0 4px;
  padding: 12px 14px;
  border-radius: 10px;
  border: 1px solid #fde68a;
  background: #fffbeb;
  cursor: pointer;
  font-size: 13px;
  color: #92400e;
  line-height: 1.45;

  input {
    margin-top: 2px;
    flex-shrink: 0;
  }

  &:has(input:disabled) {
    opacity: 0.7;
    cursor: not-allowed;
  }
`;

const DeAcordoHint = styled.span`
  display: block;
  margin-top: 4px;
  font-size: 12px;
  color: #b45309;
`;

const formatDataBR = (value) => {
  if (value == null || value === "") return "—";
  const str = String(value).trim();
  if (!str) return "—";

  const isoMatch = str.match(/^(\d{4})-(\d{2})-(\d{2})(?:[ T](\d{2}):(\d{2}))?/);
  if (isoMatch) {
    const [, y, m, d, hh, mm] = isoMatch;
    if (hh != null && mm != null) return `${d}/${m}/${y} ${hh}:${mm}`;
    return `${d}/${m}/${y}`;
  }

  return str;
};

const dash = (value) => {
  if (value == null || String(value).trim() === "") return "—";
  return String(value).trim();
};

const formatLicencaLabel = (item) =>
  `${dash(item?.fornecedor)} - ${dash(item?.tipo)}`;

const normalizeObservacoes = (value) => {
  const str = value == null ? "" : String(value).trim();
  return str;
};

export default function ModalDetalhesLicencasColaborador({ colaborador, onClose, onChanged }) {
  const api = useApi();
  const apiRef = useRef(api);
  apiRef.current = api;

  const tipo =
    String(colaborador?.tipo ?? "COLAB").toUpperCase() === "TERCEIRO" ? "TERCEIRO" : "COLAB";
  const isTerceiro = tipo === "TERCEIRO";
  const isColab = !isTerceiro;

  const matricula =
    isColab && colaborador?.matricula != null && colaborador.matricula !== "—"
      ? String(colaborador.matricula).trim()
      : "";

  const emailInicialColab =
    colaborador?.email != null && String(colaborador.email).trim() !== ""
      ? String(colaborador.email).trim().toLowerCase()
      : "";

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [removingId, setRemovingId] = useState(null);
  const [licencas, setLicencas] = useState([]);
  const [email, setEmail] = useState(emailInicialColab);
  const [emailChave, setEmailChave] = useState(emailInicialColab);
  const [nomeExibicao, setNomeExibicao] = useState(
    colaborador?.nome != null && colaborador.nome !== "—"
      ? String(colaborador.nome).trim()
      : ""
  );
  const [initialNomeExibicao, setInitialNomeExibicao] = useState(
    colaborador?.nome != null && colaborador.nome !== "—"
      ? String(colaborador.nome).trim()
      : ""
  );
  const [observacoes, setObservacoes] = useState("");
  const [initialEmail, setInitialEmail] = useState(emailInicialColab);
  const [initialObservacoes, setInitialObservacoes] = useState("");
  const [addLicencasOpen, setAddLicencasOpen] = useState(false);
  const [deAcordo, setDeAcordo] = useState(false);
  const [savingDeAcordo, setSavingDeAcordo] = useState(false);

  const colaboradorDemitido = isColab && Boolean(colaborador?.demitido);

  const chaveValida = isColab ? Boolean(matricula) : Boolean(emailChave);

  const applyContatoFromLicencas = useCallback(
    (list, nomeFromApi = null) => {
      const first = Array.isArray(list) && list.length > 0 ? list[0] : null;
      const nextEmail = first?.email != null ? String(first.email).trim().toLowerCase() : "";
      const nextObservacoes = normalizeObservacoes(first?.observacoes);
      const nextNome =
        nomeFromApi != null && String(nomeFromApi).trim() !== ""
          ? String(nomeFromApi).trim()
          : first?.nome_exibicao != null && String(first.nome_exibicao).trim() !== ""
          ? String(first.nome_exibicao).trim()
          : "";

      setEmail(nextEmail);
      setObservacoes(nextObservacoes);
      setInitialEmail(nextEmail);
      setInitialObservacoes(nextObservacoes);

      if (isTerceiro) {
        if (nextEmail) setEmailChave(nextEmail);
        setNomeExibicao(nextNome);
        setInitialNomeExibicao(nextNome);
      }
    },
    [isTerceiro]
  );

  const requestLicencas = useCallback(async () => {
    if (!chaveValida) {
      setLicencas([]);
      applyContatoFromLicencas([]);
      setLoading(false);
      return;
    }

    setLoading(true);
    try {
      const query = isColab
        ? `tipo=COLAB&matricula=${encodeURIComponent(matricula)}`
        : `tipo=TERCEIRO&email=${encodeURIComponent(emailChave)}`;

      const { data } = await apiRef.current.get(`${LICENCAS_ENDPOINT}?${query}`);

      const list = Array.isArray(data)
        ? data
        : Array.isArray(data?.licencas)
        ? data.licencas
        : data?.data ?? [];
      const parsed = Array.isArray(list) ? list : [];
      const nextDeAcordo = isColab
        ? Array.isArray(data)
          ? Boolean(colaborador?.de_acordo)
          : Boolean(data?.de_acordo)
        : false;

      setLicencas(parsed);
      setDeAcordo(nextDeAcordo);
      applyContatoFromLicencas(parsed, data?.nome_exibicao ?? null);
    } catch (err) {
      console.error(err);
      alert(err?.response?.data?.message || "Erro ao carregar licenças.");
    } finally {
      setLoading(false);
    }
  }, [
    applyContatoFromLicencas,
    chaveValida,
    colaborador?.de_acordo,
    emailChave,
    isColab,
    matricula,
  ]);

  useEffect(() => {
    requestLicencas();
  }, [requestLicencas]);

  const emailValido = useMemo(() => {
    const e = String(email || "").trim();
    if (!e) return false;
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(e);
  }, [email]);

  const nomeTerceiroValido = useMemo(() => {
    return String(nomeExibicao || "").trim().length > 0;
  }, [nomeExibicao]);

  const hasChanges = useMemo(() => {
    const emailChanged =
      String(email || "").trim().toLowerCase() !== String(initialEmail || "").trim().toLowerCase();
    const obsChanged =
      normalizeObservacoes(observacoes) !== normalizeObservacoes(initialObservacoes);
    const nomeChanged =
      isTerceiro &&
      String(nomeExibicao || "").trim() !== String(initialNomeExibicao || "").trim();
    return emailChanged || obsChanged || nomeChanged;
  }, [
    email,
    initialEmail,
    initialNomeExibicao,
    initialObservacoes,
    isTerceiro,
    nomeExibicao,
    observacoes,
  ]);

  const canSave =
    licencas.length > 0 &&
    hasChanges &&
    emailValido &&
    (!isTerceiro || nomeTerceiroValido) &&
    !saving &&
    !loading;

  const totalAtivas = licencas.length;

  const idsTiposAtivos = useMemo(
    () => licencas.map((l) => l.id_tipo_licenca).filter((id) => id != null),
    [licencas]
  );

  const handleLicencasAdicionadas = async () => {
    setAddLicencasOpen(false);
    await requestLicencas();
    onChanged?.();
  };

  const handleToggleDeAcordo = async () => {
    if (!isColab || !colaboradorDemitido || savingDeAcordo || saving) return;

    const nextValue = !deAcordo;
    const confirmar = nextValue
      ? window.confirm(
          "Marcar este colaborador como de acordo? Isso permitirá adicionar novas licenças mesmo com demissão no Protheus."
        )
      : window.confirm(
          "Remover a exceção 'de acordo'? Novas licenças voltarão a ser bloqueadas para este colaborador."
        );

    if (!confirmar) return;

    try {
      setSavingDeAcordo(true);
      const { data } = await apiRef.current.post(UPDATE_EXCECAO_ENDPOINT, {
        matricula,
        de_acordo: nextValue,
      });
      setDeAcordo(Boolean(data?.de_acordo ?? nextValue));
      onChanged?.();
    } catch (err) {
      console.error(err);
      alert(err?.response?.data?.message || "Erro ao atualizar exceção do colaborador.");
    } finally {
      setSavingDeAcordo(false);
    }
  };

  const podeAdicionarLicencas =
    emailValido &&
    (!isTerceiro || nomeTerceiroValido) &&
    (!colaboradorDemitido || deAcordo);

  const handleSalvarAlteracoes = async (e) => {
    e.preventDefault();
    if (!canSave) return;

    try {
      setSaving(true);

      const payload = {
        tipo,
        email: email.trim().toLowerCase(),
        observacoes: normalizeObservacoes(observacoes) || null,
      };

      if (isColab) {
        payload.matricula = matricula;
      } else {
        payload.email_atual = emailChave;
        payload.nome_exibicao = nomeExibicao.trim();
      }

      const { data } = await apiRef.current.post(UPDATE_CONTATO_ENDPOINT, payload);

      const nextEmailChave =
        isTerceiro && data?.email
          ? String(data.email).trim().toLowerCase()
          : emailChave;

      if (isTerceiro && nextEmailChave && nextEmailChave !== emailChave) {
        setEmailChave(nextEmailChave);
      } else {
        await requestLicencas();
      }

      onChanged?.();
    } catch (err) {
      console.error(err);
      alert(err?.response?.data?.message || "Erro ao salvar alterações.");
    } finally {
      setSaving(false);
    }
  };

  const handleRemover = async (licenca) => {
    const label = formatLicencaLabel(licenca);
    const ok = window.confirm(`Encerrar a licença "${label}"?`);
    if (!ok) return;

    try {
      setRemovingId(licenca.id);
      await apiRef.current.post(REMOVE_LICENCA_ENDPOINT, { id: licenca.id });
      await requestLicencas();
      onChanged?.();
    } catch (err) {
      console.error(err);
      alert(err?.response?.data?.message || "Erro ao encerrar licença.");
    } finally {
      setRemovingId(null);
    }
  };

  const tituloColaborador = useMemo(() => {
    if (isTerceiro) {
      const nome = dash(nomeExibicao || colaborador?.nome);
      return nome !== "—" ? nome : "Terceiro";
    }
    const nome = dash(colaborador?.nome);
    return nome !== "—" ? nome : `Matrícula ${matricula}`;
  }, [colaborador?.nome, isTerceiro, matricula, nomeExibicao]);

  if (!colaborador || !chaveValida) return null;

  return (
    <Overlay state={true}>
      <Modal header close={onClose} size="lg">
        <Formulario onSubmit={handleSalvarAlteracoes}>
          <SectionTitle>LICENÇAS ATIVAS — {tituloColaborador.toUpperCase()}</SectionTitle>

          <InfoGrid>
            <FormComp
              title="TIPO"
              type="text"
              readOnly
              valor={isTerceiro ? "TERCEIRO" : "COLABORADOR"}
            />
            {isColab ? (
              <FormComp title="MATRÍCULA" type="text" readOnly valor={matricula} />
            ) : (
              <FormComp
                title="NOME"
                type="text"
                placeholder="EX: JOÃO DA SILVA"
                valor={nomeExibicao}
                name="nome_exibicao"
                changeFunc={(e) => setNomeExibicao(e.target.value)}
                required
              />
            )}
            {isColab && (
              <>
                <FormComp title="NOME" type="text" readOnly valor={dash(colaborador.nome)} />
                <FormComp title="FUNÇÃO" type="text" readOnly valor={dash(colaborador.funcao)} />
                <FormComp
                  title="CENTRO DE CUSTO"
                  type="text"
                  readOnly
                  valor={dash(colaborador.centro_custo)}
                />
              </>
            )}
          </InfoGrid>

          {!loading && isColab && colaboradorDemitido && (
            <DeAcordoRow>
              <input
                type="checkbox"
                checked={deAcordo}
                onChange={handleToggleDeAcordo}
                disabled={savingDeAcordo || saving}
              />
              <span>
                <strong>COLABORADOR DE ACORDO</strong>
                <DeAcordoHint>
                  Desligado apenas no Protheus, mas autorizado a receber licenças no SIG.
                  {savingDeAcordo ? " Salvando..." : ""}
                </DeAcordoHint>
              </span>
            </DeAcordoRow>
          )}

          {!loading && (
            <>
              <SectionTitle>DADOS COMPARTILHADOS DAS LICENÇAS</SectionTitle>
              <SharedFields>
                <FormComp
                  title={isTerceiro ? "E-MAIL" : "E-MAIL DO COLABORADOR"}
                  type="email"
                  placeholder={
                    isTerceiro ? "EX: pessoa@empresa.com" : "EX: colaborador@palmont.com.br"
                  }
                  valor={email}
                  name="email"
                  changeFunc={(e) => setEmail(e.target.value)}
                  required
                  invalid={email.length > 0 && !emailValido}
                />
                <LargeTextareaWrap>
                  <FormComp
                    title="OBSERVAÇÕES"
                    type="textarea"
                    placeholder="INFORME OBSERVAÇÕES APLICÁVEIS A TODAS AS LICENÇAS ATIVAS"
                    valor={observacoes}
                    name="observacoes"
                    changeFunc={(e) => setObservacoes(e.target.value)}
                  />
                </LargeTextareaWrap>
              </SharedFields>
              {licencas.length > 0 && (
                <FormComp
                  type="submit"
                  valor={saving ? "SALVANDO..." : "SALVAR ALTERAÇÕES"}
                  disabled={!canSave}
                />
              )}
            </>
          )}

          {hasChanges && licencas.length > 0 && !canSave && email.length > 0 && !emailValido && (
            <p style={{ color: "#B91C1C", fontSize: "12px", marginTop: "-6px" }}>
              Informe um e-mail válido para salvar ou adicionar licenças.
            </p>
          )}

          {isTerceiro && hasChanges && !nomeTerceiroValido && (
            <p style={{ color: "#B91C1C", fontSize: "12px", marginTop: "-6px" }}>
              Informe o nome da pessoa terceira.
            </p>
          )}

          {isColab && colaboradorDemitido && !deAcordo && !loading && emailValido && (
            <p style={{ color: "#B45309", fontSize: "12px", marginTop: "-6px" }}>
              Para adicionar licenças a um colaborador desligado no Protheus, marque a opção
              &quot;Colaborador de acordo&quot; acima.
            </p>
          )}

          <SectionTitle>
            LICENÇAS ({loading ? "..." : totalAtivas} ativa{totalAtivas === 1 ? "" : "s"})
          </SectionTitle>

          {loading ? (
            <EmptyRow>Carregando licenças...</EmptyRow>
          ) : licencas.length === 0 ? (
            <EmptyRow>Nenhuma licença ativa para este cadastro.</EmptyRow>
          ) : (
            <TableShell>
              <Table>
                <thead>
                  <tr>
                    <th>LICENÇA</th>
                    <th>DATA INÍCIO</th>
                    <th style={{ width: "90px", textAlign: "center" }}>AÇÃO</th>
                  </tr>
                </thead>
                <tbody>
                  {licencas.map((licenca) => (
                    <tr key={licenca.id}>
                      <td>{formatLicencaLabel(licenca)}</td>
                      <td>{formatDataBR(licenca.data_inicio)}</td>
                      <td style={{ textAlign: "center" }}>
                        <DeleteButton
                          type="button"
                          disabled={removingId === licenca.id || saving}
                          onClick={() => handleRemover(licenca)}
                        >
                          {removingId === licenca.id ? "..." : "APAGAR"}
                        </DeleteButton>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </TableShell>
          )}

          {!loading && (
            <AddLicencasButton
              type="button"
              disabled={!podeAdicionarLicencas || saving || savingDeAcordo}
              onClick={() => setAddLicencasOpen(true)}
            >
              + ADICIONAR LICENÇAS
            </AddLicencasButton>
          )}
        </Formulario>
      </Modal>

      {addLicencasOpen && (
        <ModalAdicionarLicencasColaborador
          tipo={tipo}
          matricula={matricula || null}
          email={email}
          nomeExibicao={isTerceiro ? nomeExibicao : null}
          observacoes={observacoes}
          idsTiposAtivos={idsTiposAtivos}
          onClose={() => setAddLicencasOpen(false)}
          onSaved={handleLicencasAdicionadas}
        />
      )}
    </Overlay>
  );
}
